import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    Banknote,
    ArrowLeft,
    Edit,
    CheckCircle,
    XCircle,
    Clock,
    DollarSign,
    Building,
    Loader2,
    AlertCircle,
    CreditCard,
    RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { payoutService } from '@/services/payoutService'
import { MerchantSettlement } from '@/types'
import { safeFormatDistanceToNow } from '@/utils/date'

export default function AdminSettlementDetail() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [settlement, setSettlement] = useState<MerchantSettlement | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [actionLoading, setActionLoading] = useState<string | null>(null)

    useEffect(() => {
        if (id) {
            fetchSettlementData()
        }
    }, [id])

    const fetchSettlementData = async () => {
        if (!id) return

        try {
            setLoading(true)
            setError(null)

            const response = await payoutService.getSettlement(parseInt(id))

            if (response.success) {
                setSettlement(response.data)
            } else {
                setError('Failed to fetch settlement')
            }
        } catch (err) {
            setError('An error occurred while fetching settlement data')
            console.error('Error fetching settlement data:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleSettlementAction = async (action: string) => {
        if (!id) return

        setActionLoading(action)
        try {
            let response
            switch (action) {
                case 'mark_completed':
                    response = await payoutService.markSettlementCompleted(parseInt(id))
                    break
                case 'mark_failed':
                    response = await payoutService.markSettlementFailed(parseInt(id))
                    break
            }

            if (response?.success) {
                await fetchSettlementData()
            }
        } catch (err) {
            console.error(`Error performing ${action} on settlement:`, err)
        } finally {
            setActionLoading(null)
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'initiated':
                return 'text-blue-600 bg-blue-100 border-blue-200'
            case 'pending':
                return 'text-yellow-600 bg-yellow-100 border-yellow-200'
            case 'completed':
                return 'text-green-600 bg-green-100 border-green-200'
            case 'failed':
                return 'text-red-600 bg-red-100 border-red-200'
            default:
                return 'text-gray-600 bg-gray-100 border-gray-200'
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'initiated':
                return Clock
            case 'pending':
                return Clock
            case 'completed':
                return CheckCircle
            case 'failed':
                return XCircle
            default:
                return Clock
        }
    }

    const getMethodIcon = (method: string) => {
        switch (method) {
            case 'bank_transfer':
                return Building
            case 'crypto':
                return CreditCard
            case 'manual':
                return Banknote
            default:
                return Banknote
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: branding.primaryColor }} />
                    <p style={pageStyles.textSecondary}>Loading settlement...</p>
                </div>
            </div>
        )
    }

    if (error || !settlement) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <AlertCircle className="h-8 w-8 mx-auto mb-4" style={{ color: statusColors.error.color }} />
                    <p className="mb-4" style={{ color: statusColors.error.color }}>{error || 'Settlement not found'}</p>
                    <Button onClick={() => navigate('/admin/settlements')} variant="outline" style={{
                        color: branding.textSecondaryColor,
                        borderColor: `${branding.textSecondaryColor}40`,
                        backgroundColor: 'transparent'
                    }}>
                        Back to Settlements
                    </Button>
                </div>
            </div>
        )
    }

    const StatusIcon = getStatusIcon(settlement.status)
    const MethodIcon = getMethodIcon(settlement.method)

    return (
        <div className="min-h-screen" style={pageStyles.container}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate('/admin/settlements')}
                                style={{ color: branding.textSecondaryColor }}
                                className="hover:text-white"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to Settlements
                            </Button>
                            <div>
                                <h1 className="text-3xl font-bold" style={pageStyles.text}>Settlement #{settlement.id}</h1>
                                <p className="mt-1" style={pageStyles.textSecondary}>{settlement.merchant_name}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(settlement.status)}`}>
                                <StatusIcon className="h-4 w-4 mr-2" />
                                {settlement.status.charAt(0).toUpperCase() + settlement.status.slice(1)}
                            </span>
                            <Button
                                onClick={() => navigate(`/admin/settlements/${settlement.id}/edit`)}
                                variant="outline"
                                className="flex items-center"
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Settlement Information */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Basic Information */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h2 className="text-lg font-semibold mb-4" style={pageStyles.text}>Settlement Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>Merchant</label>
                                    <div className="mt-1 flex items-center">
                                        <Building className="h-4 w-4 mr-2" style={{ color: branding.textSecondaryColor }} />
                                        <p className="text-sm" style={pageStyles.textSecondary}>{settlement.merchant_name}</p>
                                    </div>
                                </div>
                                {settlement.submerchant_name && (
                                    <div>
                                        <label className="text-sm font-medium" style={pageStyles.textSecondary}>Submerchant</label>
                                        <p className="mt-1 text-sm" style={pageStyles.textSecondary}>{settlement.submerchant_name}</p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>Batch ID</label>
                                    <p className="mt-1 text-sm" style={pageStyles.textSecondary}>#{settlement.batch}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>Currency</label>
                                    <p className="mt-1 text-sm" style={pageStyles.textSecondary}>{settlement.currency}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium" style={pageStyles.textSecondary}>Created</label>
                                    <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                        {safeFormatDistanceToNow(settlement.created_at, 'Unknown', { addSuffix: true })}
                                    </p>
                                </div>
                                {settlement.paid_at && (
                                    <div>
                                        <label className="text-sm font-medium" style={pageStyles.textSecondary}>Paid At</label>
                                        <p className="mt-1 text-sm" style={pageStyles.textSecondary}>
                                            {safeFormatDistanceToNow(settlement.paid_at, 'Unknown', { addSuffix: true })}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Payment Details */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h2 className="text-lg font-semibold mb-4" style={pageStyles.text}>Payment Details</h2>
                            <div className="space-y-6">
                                {/* Amount */}
                                <div className="text-center p-6 rounded-lg" style={{ backgroundColor: `${statusColors.success.backgroundColor}20` }}>
                                    <div className="flex items-center justify-center mb-2">
                                        <DollarSign className="h-8 w-8" style={{ color: statusColors.success.color }} />
                                    </div>
                                    <p className="text-sm font-medium" style={pageStyles.textSecondary}>Settlement Amount</p>
                                    <p className="text-4xl font-bold" style={pageStyles.text}>
                                        ${Number(settlement.amount_fiat).toFixed(2)} {settlement.currency}
                                    </p>
                                </div>

                                {/* Payment Method */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="text-sm font-medium" style={pageStyles.textSecondary}>Payment Method</label>
                                        <div className="mt-1 flex items-center">
                                            <MethodIcon className="h-4 w-4 mr-2" style={{ color: branding.textSecondaryColor }} />
                                            <p className="text-sm" style={pageStyles.textSecondary}>
                                                {settlement.method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </p>
                                        </div>
                                    </div>
                                    {settlement.reference && (
                                        <div>
                                            <label className="text-sm font-medium" style={pageStyles.textSecondary}>Reference</label>
                                            <p className="mt-1 text-sm font-mono px-3 py-1 rounded" style={{
                                                color: branding.textSecondaryColor,
                                                backgroundColor: `${branding.textSecondaryColor}20`
                                            }}>
                                                {settlement.reference}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Notes */}
                                {settlement.notes && (
                                    <div>
                                        <label className="text-sm font-medium" style={pageStyles.textSecondary}>Notes</label>
                                        <div className="mt-1 p-3 rounded-md" style={{ backgroundColor: `${branding.textSecondaryColor}20` }}>
                                            <p className="text-sm" style={pageStyles.textSecondary}>{settlement.notes}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Status History */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h2 className="text-lg font-semibold mb-4" style={pageStyles.text}>Status History</h2>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                            <Clock className="h-4 w-4" style={{ color: branding.primaryColor }} />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium" style={pageStyles.text}>Settlement Initiated</p>
                                        <p className="text-sm" style={pageStyles.textSecondary}>
                                            {safeFormatDistanceToNow(settlement.created_at, 'Unknown', { addSuffix: true })}
                                        </p>
                                    </div>
                                </div>

                                {settlement.status === 'pending' && (
                                    <div className="flex items-center space-x-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${statusColors.warning.backgroundColor}20` }}>
                                                <Clock className="h-4 w-4" style={{ color: statusColors.warning.color }} />
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium" style={pageStyles.text}>Payment Pending</p>
                                            <p className="text-sm" style={pageStyles.textSecondary}>Awaiting payment processing</p>
                                        </div>
                                    </div>
                                )}

                                {settlement.status === 'completed' && (
                                    <div className="flex items-center space-x-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${statusColors.success.backgroundColor}20` }}>
                                                <CheckCircle className="h-4 w-4" style={{ color: statusColors.success.color }} />
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium" style={pageStyles.text}>Payment Completed</p>
                                            <p className="text-sm" style={pageStyles.textSecondary}>
                                                {settlement.paid_at && safeFormatDistanceToNow(settlement.paid_at, 'Unknown', { addSuffix: true })}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {settlement.status === 'failed' && (
                                    <div className="flex items-center space-x-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${statusColors.error.backgroundColor}20` }}>
                                                <XCircle className="h-4 w-4" style={{ color: statusColors.error.color }} />
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium" style={pageStyles.text}>Payment Failed</p>
                                            <p className="text-sm" style={pageStyles.textSecondary}>Payment could not be processed</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions Sidebar */}
                    <div className="space-y-6">
                        {/* Settlement Actions */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>Settlement Actions</h3>
                            <div className="space-y-3">
                                {settlement.status === 'pending' && (
                                    <>
                                        <Button
                                            onClick={() => handleSettlementAction('mark_completed')}
                                            disabled={actionLoading === 'mark_completed'}
                                            className="w-full flex items-center justify-center"
                                            style={pageStyles.primaryBg}
                                        >
                                            {actionLoading === 'mark_completed' ? (
                                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <CheckCircle className="h-4 w-4 mr-2" />
                                            )}
                                            Mark as Completed
                                        </Button>
                                        <Button
                                            onClick={() => handleSettlementAction('mark_failed')}
                                            disabled={actionLoading === 'mark_failed'}
                                            variant="destructive"
                                            className="w-full flex items-center justify-center"
                                        >
                                            {actionLoading === 'mark_failed' ? (
                                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <XCircle className="h-4 w-4 mr-2" />
                                            )}
                                            Mark as Failed
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Settlement Status */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>Status Information</h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm" style={pageStyles.textSecondary}>Current Status</span>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(settlement.status)}`}>
                                        <StatusIcon className="h-3 w-3 mr-1" />
                                        {settlement.status.charAt(0).toUpperCase() + settlement.status.slice(1)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm" style={pageStyles.textSecondary}>Created</span>
                                    <span className="text-sm" style={pageStyles.textSecondary}>
                                        {safeFormatDistanceToNow(settlement.created_at, 'Unknown', { addSuffix: true })}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm" style={pageStyles.textSecondary}>Last Updated</span>
                                    <span className="text-sm" style={pageStyles.textSecondary}>
                                        {safeFormatDistanceToNow(settlement.updated_at, 'Unknown', { addSuffix: true })}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Related Information */}
                        <div className="border backdrop-blur-md shadow-sm p-6" style={cardStyles}>
                            <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>Related Information</h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm" style={pageStyles.textSecondary}>Payout Batch</span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => navigate(`/admin/payout-batches/${settlement.batch}`)}
                                        style={{ color: branding.primaryColor }}
                                        className="hover:opacity-80"
                                    >
                                        View Batch #{settlement.batch}
                                    </Button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm" style={pageStyles.textSecondary}>Merchant</span>
                                    <span className="text-sm" style={pageStyles.textSecondary}>{settlement.merchant_name}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
